import React, { useState, useEffect } from 'react';
import { useMap, Map as Gmap, InfoWindow } from '@vis.gl/react-google-maps';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM } from '../../constants';
import Marker from './Marker';
import GPXMarker from './GPXMarker';
import GPXPointTooltip from './GPXPointTooltip';
import { Polyline as EPolyline } from './external/Polyline';
import MapHandler from './MapHandler';
import './Polyline.scss';

const Map = ({ gpxPoints, gpxCoordinates, snappedCoordinates }) => {
  const map = useMap();
  const [infoWindow, setInfoWindow] = useState(null);
  const [activeGPXMarkerIndex, setActiveGPXMarkerIndex] = useState(null);
  const [snappedPolylinePath, setSnappedPolylinePath] = useState(null);
  const geometryLibrary = useMapsLibrary('geometry');

  // Handle GPX marker click
  const handleGPXMarkerClick = (pointData, index) => {
    setActiveGPXMarkerIndex(index);
    setInfoWindow({
      position: { lat: pointData.lat, lng: pointData.lng },
      content: <GPXPointTooltip pointData={pointData} />,
    });
  };

  // Encode snapped coordinates for polyline display
  useEffect(() => {
    if (!snappedCoordinates || snappedCoordinates.length === 0) {
      setSnappedPolylinePath(null);
      return;
    }

    if (!geometryLibrary || !window.google?.maps) {
      return;
    }

    try {
      // Convert coordinates to google.maps.LatLng objects for encoding
      const path = snappedCoordinates.map((coord) => new window.google.maps.LatLng(coord.lat, coord.lng));
      const encodedPath = geometryLibrary.encoding.encodePath(path);
      setSnappedPolylinePath(encodedPath);
    } catch (error) {
      console.error('Error encoding snapped path:', error);
      setSnappedPolylinePath(null);
    }
  }, [snappedCoordinates, geometryLibrary]);

  const handleSnappedPolylineClick = (event) => {
    const position = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng(),
    };
    setInfoWindow({
      position,
      content: (
        <div className="gpx-info-window">
          <div className="content">
            <div className="title">
              <b>Snapped to Roads</b>
            </div>
            <div className="info">
              <p>{snappedCoordinates.length} snapped points</p>
            </div>
          </div>
        </div>
      ),
    });
  };

  return (
    <Gmap
      mapId={process.env.REACT_APP_GOOGLE_MAP_ID}
      defaultZoom={DEFAULT_MAP_ZOOM}
      defaultCenter={DEFAULT_MAP_CENTER}
      gestureHandling="greedy"
      disableDefaultUI={true}
    >
      <MapHandler map={map} gpxCoordinates={gpxCoordinates} />

      {/* Display GPX markers (blue dots) */}
      {gpxPoints && gpxPoints.length > 0 && (
        <>
          {gpxPoints.map((point, index) => (
            <GPXMarker
              key={`gpx-marker-${index}`}
              position={{ lat: point.lat, lng: point.lng }}
              onClick={() => handleGPXMarkerClick(point, index)}
              active={activeGPXMarkerIndex === index}
            />
          ))}
        </>
      )}

      {/* Display snapped to roads polyline if coordinates are available */}
      {snappedPolylinePath && geometryLibrary && (
        <EPolyline
          key={`snapped-polyline-${snappedPolylinePath.substring(0, 20)}`}
          encodedPath={snappedPolylinePath}
          strokeColor="#2196F3"
          strokeOpacity={1.0}
          strokeWeight={5}
          onClick={handleSnappedPolylineClick}
        />
      )}

      {/* Show start and end markers for GPX track */}
      {gpxCoordinates && gpxCoordinates.length > 0 && (
        <>
          {gpxCoordinates[0] && <Marker type="OriginMarker" position={gpxCoordinates[0]} />}
          {gpxCoordinates.length > 1 && gpxCoordinates[gpxCoordinates.length - 1] && (
            <Marker type="DestinationMarker" position={gpxCoordinates[gpxCoordinates.length - 1]} />
          )}
        </>
      )}
      {infoWindow && (
        <InfoWindow
          key={infoWindow.position.lat + ',' + infoWindow.position.lng}
          position={infoWindow.position}
          onCloseClick={() => {
            setInfoWindow(null);
            setActiveGPXMarkerIndex(null);
          }}
        >
          {infoWindow.content}
        </InfoWindow>
      )}
    </Gmap>
  );
};

export default React.memo(Map);
